/**
 * As duas listas do rateio do grupo, declaradas pela tela (prévia aprovada em 06/10/2026:
 * https://claude.ai/artifact/CkY7t7PcWW9VTnVUTK4r8x).
 *
 *  - `produtoIds` é O QUE SE DIVIDE: os itens que o grupo usa junto; somados, formam o valor repartido.
 *  - `produtoIdsBase` é O QUE DECIDE QUANTO CADA UM PAGA: a quantidade de cada empresa nesses itens.
 *
 * ⛔ O CONTRATO QUE APAGA DADO SE ERRAR: no `simular` e no `gravar`, campo AUSENTE quer dizer "não
 * mexe" e `[]` quer dizer "ESVAZIA". A tela manda SEMPRE as duas listas, explícitas, mesmo a que não
 * mudou — e `[]` nunca sai por omissão. `corpoDasListas` é o único lugar que monta isso.
 *
 * ⛔ Nada aqui soma dinheiro. Os candidatos são nome e QUAIS EMPRESAS TÊM o produto; valores vêm da
 * simulação do backend, com nome. (A branch `escolha-itens-rateados` somava `valorMensal` no front e foi
 * descartada por isso: PR #301.)
 */
import { CriterioDeRateio } from './rateioDoGrupo';

export type ListaDoRateio = 'divide' | 'decide';

export interface Selecao {
  /** O que se divide. */
  divide: number[];
  /** O que decide quanto cada um paga. */
  decide: number[];
}

export interface CandidatoDoGrupo {
  productId: number;
  productCode: string | null;
  nome: string;
  /** Empresas do grupo que têm o produto em assinatura ativa. */
  empresas: number[];
}

interface ItemDeAssinatura {
  productId?: number | null;
  productCode?: string | null;
  name?: string | null;
  type?: string | null;
}
interface AssinaturaDoGrupo {
  clientId: number;
  status?: string | null;
  items?: ItemDeAssinatura[] | null;
}

/** O nome do item sem o sufixo de exibição do rateio ("(rateio 68,89%)"). */
const semSufixoDoRateio = (s: string) => s.replace(/\s*\(rateio[^)]*\)\s*$/i, '').trim();

/**
 * Produtos MRR das assinaturas ATIVAS das empresas do grupo: o que pode entrar numa das listas.
 * Só nome e titularidade — nenhum valor.
 */
export function candidatosDoGrupo(contracts: AssinaturaDoGrupo[], empresas: number[]): CandidatoDoGrupo[] {
  const doGrupo = new Set(empresas);
  const porProduto = new Map<number, CandidatoDoGrupo>();
  for (const c of contracts || []) {
    if (!doGrupo.has(c.clientId) || c.status !== 'Ativo') continue;
    for (const i of c.items || []) {
      if (i.type !== 'MRR' || !i.productId) continue;
      const atual = porProduto.get(i.productId) || {
        productId: i.productId,
        productCode: i.productCode || null,
        nome: semSufixoDoRateio(String(i.name || i.productCode || `produto ${i.productId}`)),
        empresas: [],
      };
      if (!atual.empresas.includes(c.clientId)) atual.empresas.push(c.clientId);
      porProduto.set(i.productId, atual);
    }
  }
  return Array.from(porProduto.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Em qual lista o produto está (ou nenhuma). */
export function listaDoProduto(selecao: Selecao, productId: number): ListaDoRateio | null {
  if (selecao.divide.includes(productId)) return 'divide';
  if (selecao.decide.includes(productId)) return 'decide';
  return null;
}

/**
 * Põe o produto numa lista, ou em nenhuma. NUNCA nas duas: o mesmo produto não pode ser o bolo e a
 * régua ao mesmo tempo.
 */
export function moverProduto(selecao: Selecao, productId: number, destino: ListaDoRateio | null): Selecao {
  const divide = selecao.divide.filter((id) => id !== productId);
  const decide = selecao.decide.filter((id) => id !== productId);
  if (destino === 'divide') divide.push(productId);
  if (destino === 'decide') decide.push(productId);
  return { divide, decide };
}

/**
 * O que impede simular a seleção. Vazio = pode simular.
 * - o que se divide nunca vazio (o backend também recusa);
 * - critério por ramais sem nada que decida não tem peso para dividir;
 * - modelo afirmado ou não declarado: a lista do que se divide não muda.
 */
export function problemasDaSelecao(
  selecao: Selecao,
  criterio: CriterioDeRateio,
  podeEditarOQueSeDivide: boolean,
  original: Selecao,
): string[] {
  const p: string[] = [];
  if (!selecao.divide.length) p.push('O que se divide não pode ficar vazio.');
  if (criterio === 'ramais_contratados' && !selecao.decide.length) {
    p.push('Com o critério por ramais, é preciso pelo menos um item que decida quanto cada empresa paga.');
  }
  if (!podeEditarOQueSeDivide && !mesmaLista(selecao.divide, original.divide)) {
    p.push('Neste grupo a lista do que se divide não se edita aqui.');
  }
  return p;
}

const mesmaLista = (a: number[], b: number[]) => {
  const x = [...a].sort((m, n) => m - n);
  const y = [...b].sort((m, n) => m - n);
  return x.length === y.length && x.every((v, k) => v === y[k]);
};

/**
 * ⛔ O corpo das listas para `simular` e `gravar`: as DUAS, sempre explícitas, cópias ordenadas.
 * Ausente = "não mexe" e `[]` = "esvazia" no backend; por isso nunca se omite uma e nunca se manda
 * `[]` para dizer "não mudei". Lista vazia só sai se a seleção estiver vazia de verdade, e aí
 * `problemasDaSelecao` já travou antes.
 */
export function corpoDasListas(selecao: Selecao): { produtoIds: number[]; produtoIdsBase: number[] } {
  return {
    produtoIds: [...selecao.divide].sort((a, b) => a - b),
    produtoIdsBase: [...selecao.decide].sort((a, b) => a - b),
  };
}
